import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  IActivo,
  IEmergencia,
  IFilter,
  IGeoJSONCircle,
  IGeoJSONPoint,
  IPersonalSalud,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  IUbicacion,
  IUbicacionBase,
  IUsuario,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { CardHeaderComponent } from '../../../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../../../componentes/content-card/content-card.component';
import { LoadingComponent } from '../../../../../../componentes/loading/loading.component';
import { UsuarioSelectorComponent } from '../../../../../../componentes/selectores/usuario-selector/usuario-selector.component';
import { ButtonComponent } from '../../../../../../auxiliares/button/button.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { IDatosAsignacionEmergencia } from '../../../../../../auxiliares/servicios/http/emergencias.service';
import { LoginService } from '../../../../../login/login.service';
import {
  ESTADOS_FINALES,
  IPresentacion,
  presentacionEstado,
  presentacionPrioridad,
} from '../../../estado-emergencia';
import { haceCuanto, nombrePaciente } from '../../emergencia.service';
import {
  COLOR_COMUNICACION,
  IMovilEmergencia,
  distanciaFormateada,
} from '../movil-emergencia';
import { SelectorMovilEmergenciaComponent } from '../../selector-movil-emergencia/selector-movil-emergencia.component';

export interface IAsignarEmergenciaDialogData {
  emergencia: IEmergencia;
  reasignar: boolean;
  /** Llama a la API; si falla, el diálogo queda abierto con el error. */
  guardar: (datos: IDatosAsignacionEmergencia) => Promise<unknown>;
}

@Component({
  selector: 'app-asignar-emergencia-dialog',
  templateUrl: './asignar-emergencia-dialog.component.html',
  styleUrl: '../acciones-emergencia-dialog.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipDirective, 
    ReactiveFormsModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    
    NgSelectModule,
    CardHeaderComponent,
    ContentCardComponent,
    LoadingComponent,
    UsuarioSelectorComponent,
    ButtonComponent,
  ],
})
export class AsignarEmergenciaDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<AsignarEmergenciaDialogComponent>);
  private dialog = inject(MatDialog);
  private lista2 = inject(Lista2Service);
  public helper = inject(HelperService);
  public data = inject<IAsignarEmergenciaDialogData>(MAT_DIALOG_DATA);

  private clientePropio = LoginService.getCliente();
  public emergencia = this.data.emergencia;
  public reasignar = this.data.reasignar;

  public cargando = true;
  public guardando = false;
  public form!: FormGroup;

  public moviles: IMovilEmergencia[] = [];
  public medicos: IPersonalSalud[] = [];
  public enfermeros: IPersonalSalud[] = [];
  public hospitales: IUbicacionBase<'Hospital'>[] = [];
  public bases: IUbicacionBase<'Centro de Atención'>[] = [];
  public usuariosMovil: IUsuario[] = [];

  public titulo = '';
  public subtitulo = '';
  public prioridad: IPresentacion = presentacionPrioridad(
    this.emergencia.prioridadCierre || this.emergencia.prioridadApertura,
  );
  public estado: IPresentacion = presentacionEstado(
    this.emergencia.ultimoEventoEmergencia?.estado,
  );
  public paciente = nombrePaciente(this.emergencia.destinatarioAsistencia);
  public distanciaFormateada = distanciaFormateada;

  public get movilSeleccionado(): IMovilEmergencia | undefined {
    const id = this.form?.value?.idMovilAsignado;
    return this.moviles.find((m) => m._id === id);
  }

  // Carga

  private async listarMoviles(): Promise<void> {
    const populate: IPopulate[] = [
      { path: 'tracker', select: 'nombre tipo frecReporte' },
    ];
    const filter: IFilter<IActivo> = { funcion: 'Ambulancia' };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      sort: 'identificacion',
      unlimited: true,
      includeChildren: true,
    };
    const [vehiculos, activas] = await Promise.all([
      firstValueFrom(this.lista2.vehiculos(query)),
      this.listarAsignacionesActivas(),
    ]);
    const datos = (vehiculos?.datos ?? []) as IActivo[];
    const reportes = await this.ultimosReportes(datos.map((v) => v._id!));
    const destino = this.emergencia.ubicacionDestino?.coordinates as
      | [number, number]
      | undefined;

    this.moviles = datos
      .map((v): IMovilEmergencia => {
        const reporte = reportes.find((r) => r.idsAsignados?.includes(v._id!));
        const posicion = reporte?.valores?.geojson as IGeoJSONPoint | undefined;
        const asignadaA = activas.get(v._id!);
        const movil: IMovilEmergencia = {
          ...v,
          ultimoReporte: reporte,
          posicion,
          asignadaA,
          disabled: asignadaA !== undefined,
          ...this.comunicacion(v, reporte),
        };
        if (destino && posicion?.coordinates) {
          movil.distancia = this.helper.distanciaEntrePuntos(
            destino,
            posicion.coordinates as [number, number],
          );
        }
        return movil;
      })
      .sort((a, b) => {
        // Disponibles primero, después por cercanía
        if (!!a.disabled !== !!b.disabled) return a.disabled ? 1 : -1;
        return (a.distancia ?? Infinity) - (b.distancia ?? Infinity);
      });
  }

  /** idMovil → código de la otra emergencia activa que lo tiene asignado. */
  private async listarAsignacionesActivas(): Promise<Map<string, number>> {
    const filter = {
      tipo: 'Médica',
      _id: { $ne: this.emergencia._id },
      'ultimoEventoEmergencia.estado': { $nin: ESTADOS_FINALES },
      'ultimoEventoEmergencia.detallesEmergencias.idMovilAsignado': {
        $exists: true,
      },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'codigo ultimoEventoEmergencia',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.emergencias(query));
    const mapa = new Map<string, number>();
    for (const e of res?.datos ?? []) {
      const id = e.ultimoEventoEmergencia?.detallesEmergencias?.idMovilAsignado;
      if (id) mapa.set(id, e.codigo!);
    }
    return mapa;
  }

  private async ultimosReportes(
    ids: string[],
  ): Promise<IReporteBase<'Tracker 4G'>[]> {
    if (!ids.length) return [];
    const filter: IFilter<IReporteGenerico> = { idsAsignados: { $in: ids } };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    const res = await firstValueFrom(
      this.lista2.ultimosReportesGenericos<'Tracker 4G'>(query),
    );
    return (res?.datos ?? []) as IReporteBase<'Tracker 4G'>[];
  }

  private comunicacion(
    v: IActivo,
    reporte?: IReporteBase<'Tracker 4G'>,
  ): Pick<IMovilEmergencia, 'colorComunicacion' | 'textoComunicacion'> {
    if (!reporte?.fechaCreacion) {
      return {
        colorComunicacion: COLOR_COMUNICACION.sinReporte,
        textoComunicacion: 'Sin reportes',
      };
    }
    const frecMin = v.tracker?.frecReporte || 5;
    const diff = Date.now() - new Date(reporte.fechaCreacion).getTime();
    const frecMs = frecMin * 60 * 1000;
    const color =
      diff > 2 * frecMs
        ? COLOR_COMUNICACION.sinComunicar
        : diff > frecMs
          ? COLOR_COMUNICACION.demorada
          : COLOR_COMUNICACION.reciente;
    return {
      colorComunicacion: color,
      textoComunicacion: `Reportó ${haceCuanto(reporte.fechaCreacion)}`,
    };
  }

  private async listarPersonal(): Promise<void> {
    const filter: IFilter<IPersonalSalud> = {
      idCliente: this.clientePropio?._id,
      rol: { $in: ['Médico', 'Enfermero'] },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select: 'nombre matricula rol',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.personalSaluds(query));
    const datos = res?.datos ?? [];
    this.medicos = datos.filter((p) => p.rol === 'Médico');
    this.enfermeros = datos.filter((p) => p.rol === 'Enfermero');
  }

  private async listarHospitales(): Promise<void> {
    const filter: IFilter<IUbicacion> = { idCliente: this.clientePropio?._id };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'identificacion',
      select: 'identificacion valores.gestion',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.hospitals(query));
    this.hospitales = (res?.datos ?? []) as IUbicacionBase<'Hospital'>[];
  }

  private async listarBases(): Promise<void> {
    const filter = { 'valores.tipoEmergencia': 'Médica' };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'identificacion',
      select: 'identificacion geojson',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.centroAtencions(query));
    this.bases = (res?.datos ?? []) as IUbicacionBase<'Centro de Atención'>[];
  }

  private async listarUsuariosMovil(): Promise<void> {
    const query: IQueryParam = {
      sort: 'usuario',
      select: 'usuario idCliente',
      unlimited: true,
    };
    const res = await firstValueFrom(
      this.lista2.usuarioMovilEmergencias(query),
    );
    this.usuariosMovil = res?.datos ?? [];
  }

  // Formulario

  private crearForm(): void {
    // Al reasignar se parte de lo asignado en el último evento
    const det = this.reasignar
      ? this.emergencia.ultimoEventoEmergencia?.detallesEmergencias
      : undefined;
    this.form = new FormGroup({
      idMovilAsignado: new FormControl<string | null>(
        det?.idMovilAsignado ?? null,
        Validators.required,
      ),
      idChofer: new FormControl<string | null>(det?.idChofer ?? null),
      idMovilUsuario: new FormControl<string | null>(
        det?.idMovilUsuario ?? null,
      ),
      idsMedicos: new FormControl<string[]>(det?.idsMedicos ?? []),
      idsEnfermeros: new FormControl<string[]>(det?.idsEnfermeros ?? []),
      idHospital: new FormControl<string | null>(det?.idHospital ?? null),
      idCentroDeAtencion: new FormControl<string | null>(
        det?.idCentroDeAtencion ?? null,
      ),
      observaciones: new FormControl<string>(''),
      motivoReasignacion: new FormControl<string>(
        '',
        this.reasignar ? Validators.required : [],
      ),
    });
  }

  public onMovilChange(movil?: IMovilEmergencia | null): void {
    if (!movil?.posicion?.coordinates) return;
    // Si el móvil está dentro de una base de operaciones, se la propone
    const base = this.baseQueContiene(
      movil.posicion.coordinates as [number, number],
    );
    if (base && !this.form.value.idCentroDeAtencion) {
      this.form.patchValue({ idCentroDeAtencion: base._id });
    }
  }

  private baseQueContiene(
    punto: [number, number],
  ): IUbicacionBase<'Centro de Atención'> | undefined {
    return this.bases.find((b) => {
      const circulo = b.geojson as IGeoJSONCircle;
      if (!circulo?.coordinates || circulo.radius == null) return false;
      return HelperService.estaDentroDelRadio(
        punto,
        circulo.coordinates as [number, number],
        circulo.radius,
      );
    });
  }

  public async elegirEnMapa(): Promise<void> {
    const ref = this.dialog.open(SelectorMovilEmergenciaComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: {
        emergencia: this.emergencia,
        moviles: this.moviles,
        idSeleccionado: this.form.value.idMovilAsignado,
      },
    });
    const movil = (await firstValueFrom(ref.afterClosed())) as
      | IMovilEmergencia
      | undefined;
    if (!movil) return;
    this.form.patchValue({ idMovilAsignado: movil._id });
    this.onMovilChange(movil);
  }

  public async guardar(): Promise<void> {
    if (this.form.invalid || this.guardando) return;
    const v = this.form.value;
    const datos: IDatosAsignacionEmergencia = {
      idMovilAsignado: v.idMovilAsignado,
      idChofer: v.idChofer || undefined,
      idMovilUsuario: v.idMovilUsuario || undefined,
      idsMedicos: v.idsMedicos ?? [],
      idsEnfermeros: v.idsEnfermeros ?? [],
      idHospital: v.idHospital || undefined,
      idCentroDeAtencion: v.idCentroDeAtencion || undefined,
      ubicacionActual: this.movilSeleccionado?.posicion,
      observaciones: v.observaciones?.trim() || undefined,
    };
    if (this.reasignar) {
      datos.motivoReasignacion = v.motivoReasignacion?.trim();
    }
    this.guardando = true;
    try {
      await this.data.guardar(datos);
      this.dialogRef.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  public cerrar(): void {
    this.dialogRef.close(false);
  }

  async ngOnInit(): Promise<void> {
    const codigo = this.emergencia.codigo ?? '';
    this.titulo = this.reasignar
      ? `Reasignar la emergencia ${codigo}`
      : `Asignar la emergencia ${codigo}`;
    this.subtitulo = this.reasignar
      ? 'Cambiá el móvil, el personal o el destino'
      : 'Elegí el móvil y el personal que van a atender';
    this.crearForm();
    try {
      await Promise.all([
        this.listarMoviles(),
        this.listarPersonal(),
        this.listarHospitales(),
        this.listarBases(),
        this.listarUsuariosMovil(),
      ]);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.cargando = false;
  }
}
